/**
 * A room's outline in the 3D view (z8nrz7fw25): a rectangle, or an L, U or T
 * shaped room — a rectangle `w` × `d` cells with part cut away. Pure, so what
 * is floor, where the walls run and whether a unit fits are tested rules, and
 * the floor, room and drag code all agree on them.
 */

export const ROOM_OUTLINES = ['rect', 'L', 'L-left', 'U', 'T'] as const
export type RoomOutline = (typeof ROOM_OUTLINES)[number]

export const ROOM_OUTLINE_LABELS: Record<RoomOutline, string> = {
  rect: 'Rectangle',
  L: 'L shape',
  'L-left': 'L shape, mirrored',
  U: 'U shape',
  T: 'T shape',
}

export interface Rect {
  x: number
  y: number
  w: number
  d: number
}

export function toOutline(value: string | null | undefined): RoomOutline {
  return (ROOM_OUTLINES as readonly string[]).includes(value ?? '') ? (value as RoomOutline) : 'rect'
}

/**
 * The parts cut away from the `w` × `d` rectangle. The back is y = 0, the
 * front y = d. Too small a room to cut keeps its rectangle.
 */
export function cutouts(outline: RoomOutline, w: number, d: number): Rect[] {
  const half = (n: number) => Math.floor(n / 2)
  if (outline === 'rect' || w < 2 || d < 2) return []
  if (outline === 'L') return [{ x: w - half(w), y: 0, w: half(w), d: half(d) }]
  if (outline === 'L-left') return [{ x: 0, y: 0, w: half(w), d: half(d) }]
  if (outline === 'U') {
    if (w < 3) return []
    const side = Math.ceil(w / 3)
    return [{ x: side, y: 0, w: w - 2 * side, d: half(d) }].filter((r) => r.w > 0)
  }
  // T: the bar across the back, the stem down the middle.
  if (w < 3) return []
  const arm = Math.floor(w / 3)
  const stem = d - half(d)
  return [
    { x: 0, y: half(d), w: arm, d: stem },
    { x: w - arm, y: half(d), w: arm, d: stem },
  ]
}

const overlap = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.d && b.y < a.y + a.d

/** Whether the cell at (cx, cy) is floor. */
export function isFloor(cx: number, cy: number, outline: RoomOutline, w: number, d: number): boolean {
  if (cx < 0 || cy < 0 || cx >= w || cy >= d) return false
  return !cutouts(outline, w, d).some((c) => overlap({ x: cx, y: cy, w: 1, d: 1 }, c))
}

/** Whether a footprint stands wholly on the room's floor. */
export function fitsInside(box: Rect, outline: RoomOutline, w: number, d: number): boolean {
  if (box.x < 0 || box.y < 0 || box.x + box.w > w || box.y + box.d > d) return false
  return !cutouts(outline, w, d).some((c) => overlap(box, c))
}

/** The floor as rectangles to draw: each row's runs of floor cells. */
export function floorRects(outline: RoomOutline, w: number, d: number): Rect[] {
  const out: Rect[] = []
  for (let y = 0; y < d; y++) {
    let x = 0
    while (x < w) {
      if (!isFloor(x, y, outline, w, d)) {
        x++
        continue
      }
      const start = x
      while (x < w && isFloor(x, y, outline, w, d)) x++
      out.push({ x: start, y, w: x - start, d: 1 })
    }
  }
  return out
}

/** A run of wall, in cells, and the side of the room it faces from. */
export interface WallRun {
  /** back and left are drawn full height, front and right low: a cut-away. */
  side: 'back' | 'front' | 'left' | 'right'
  x: number
  y: number
  /** Length along the wall: across for back/front, deep for left/right. */
  length: number
}

/** Every wall of the room: a floor cell's edge with no floor beyond it, joined into runs. */
export function wallRuns(outline: RoomOutline, w: number, d: number): WallRun[] {
  const runs: WallRun[] = []
  const floor = (x: number, y: number) => isFloor(x, y, outline, w, d)
  // Along x: back walls (no floor behind) and front walls (no floor in front).
  for (let y = 0; y < d; y++) {
    for (const side of ['back', 'front'] as const) {
      let x = 0
      while (x < w) {
        const edge = floor(x, y) && !floor(x, side === 'back' ? y - 1 : y + 1)
        if (!edge) {
          x++
          continue
        }
        const start = x
        while (x < w && floor(x, y) && !floor(x, side === 'back' ? y - 1 : y + 1)) x++
        runs.push({ side, x: start, y: side === 'back' ? y : y + 1, length: x - start })
      }
    }
  }
  // Along y: left and right walls.
  for (let x = 0; x < w; x++) {
    for (const side of ['left', 'right'] as const) {
      let y = 0
      while (y < d) {
        const edge = floor(x, y) && !floor(side === 'left' ? x - 1 : x + 1, y)
        if (!edge) {
          y++
          continue
        }
        const start = y
        while (y < d && floor(x, y) && !floor(side === 'left' ? x - 1 : x + 1, y)) y++
        runs.push({ side, x: side === 'left' ? x : x + 1, y: start, length: y - start })
      }
    }
  }
  return runs
}
